import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  RowSelectionOptions,
  GetRowIdFunc,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ValueFormatterParams,
  CellEditingStoppedEvent,
  RowClassParams,
  NewValueParams,
  GridOptions,
  ICellRendererParams,
  IRowNode,
  CellChangedEvent,
  CellValueChangedEvent,
  ICellEditorParams,
} from 'ag-grid-community'; // Column Definition Type Interface
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

import {
  CountyAttributes,
  PlotAttributes,
  StateAttributes,
  TripAttributes,
} from '~app/models';
import { CellEditorComponent } from '~app/components/shared/cell-editor/cell-editor.component';
import { PinnedCellRendererComponent } from '~app/components/shared/pinned-cell-renderer/pinned-cell-renderer.component';
import { CellRendererComponent } from '~app/components/shared/cell-renderer/cell-renderer.component';
import { TripService } from '~app/services';
import { WaypointNameCellRenderer } from '~app/components/shared/cell-renderer/waypoint-name-cell-renderer.component';

import { RowOptionsComponent } from '../row-options/row-options.component';
import { DateTimeCellRenderer } from '~app/components/shared/cell-renderer/date-cell-renderer.component';
import { ScheduledCheckpointModalComponent } from '../scheduled-checkpoint-modal/scheduled-checkpoint-modal.component';
import { CheckpointAttributes } from '~app/models/checkpoint';
import { CheckpointDescriptionCellRenderer } from '~app/components/shared/cell-renderer/checkpoint-description-cell-renderer.component';
import { SaveBtnComponent } from '~app/components/shared/cell-renderer/save-btn-renderer.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormsUtility } from '~app/utilities';
import { NoRowsMessageComponent } from '~app/components/shared/no-rows-message.component';

/** Internal Name: Checkpoints.  User-facing Name: I'm Okays */
@Component({
  selector: 'ciao-checkpoint-grid',
  imports: [AgGridAngular],
  templateUrl: './checkpoint-grid.component.html',
  styleUrl: './checkpoint-grid.component.less',
})
export class CheckpointGridComponent {
  constructor(
    private readonly tripService: TripService,
    private readonly toastr: ToastrService
  ) {}
  rowData: CheckpointAttributes[];
  tripIsOpen: boolean;
  tripDates: Pick<TripAttributes, 'startDate' | 'endDate'>;
  readonly tripId$ = new BehaviorSubject<string>(null);
  @Input() set tripId(val: string) {
    this.tripId$.next(val);
  }
  get tripId() {
    return this.tripId$.value;
  }

  readonly tripDataSubscription = this.tripId$
    .pipe(
      filter((id) => !!id),
      switchMap((id) => this.tripService.findById(id)),
      tap((trip) => {
        this.tripIsOpen = ['Open', 'Late'].includes(trip.tripStatus);
        this.rowData = trip.checkpoints;
        this.tripDates = trip;
      })
    )
    .subscribe();

  @Input() defaultColDef: ColDef;
  private _readOnly = false;
  /** When true (e.g. the trip is Closed) lock the grid: no cell editing, no
   * add-row, no row delete. The backend also rejects edits to closed trips. */
  @Input() set readOnly(value: boolean) {
    this._readOnly = !!value;
    this.applyReadOnly();
  }
  get readOnly(): boolean {
    return this._readOnly;
  }

  @ViewChild('checkPointModal')
  checkPointModal: ScheduledCheckpointModalComponent;
  @Output()
  checkpointClickEvent = new EventEmitter<any>();
  /** emit null if no errors.  emit _something_ (either array of errors, or just 'true') if there are errors */
  @Output() errors = new EventEmitter<any>();

  @Input() defaultRowSelection: RowSelectionOptions | 'single' | 'multiple';
  private subscriptions: Subscription = new Subscription();

  gridApi!: GridApi;
  /** Input for new row */
  inputRow: Partial<CheckpointAttributes> = {};
  pinnedTopRowData = [this.inputRow];
  allowSaveOnPinned: boolean = false;

  readonly testFormGroup = new FormGroup(
    {
      id: new FormControl(''),
      description: new FormControl('', [
        Validators.required,
        FormsUtility.isOverCharLimit,
      ]),
      expectedTime: new FormControl<string>(null, [
        Validators.required,
        FormsUtility.inBetweenStartAndEnd(() => this.tripDates),
      ]),
      //status: new FormControl<string>(null), // replace when we have status again
    },
    [FormsUtility.combineChildValidation]
  );

  checkpointColDefs: ColDef[] = [
    {
      field: 'id',
      hide: true,
    },
    {
      headerName: "Schedule I'm Okay Alert (Optional)",
      field: 'description',
      flex: 3, // description takes the larger share; date & time is flex 2
      minWidth: 250,
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'text',
        placeholder: 'Alert Description (*Required to save)',
        validators: [this.testFormGroup.controls.description.validator],
      },
      cellRendererSelector: (p: ICellRendererParams) => ({
        component: p.node?.rowPinned
          ? PinnedCellRendererComponent
          : CheckpointDescriptionCellRenderer,
      }),
      cellRendererParams: {
        placeholder: 'Alert Description (*Required to save)',
        onCheckpointClick: (rowNode: IRowNode) => {
          if (!this.tripIsOpen && !rowNode?.data.complete) {
            this.checkpointClickEvent?.emit(rowNode);
          }
        },
        checkpointSubmitted: () => {
          return false;
        },
      },
      equals: (a, b) => false,
      editable: (params) => !params.data.complete,
      wrapText: true,
    },
    {
      headerName: 'Alert Date & Time',
      field: 'expectedTime',
      flex: 2, // narrower than description so date & time aren't spread far apart
      minWidth: 210,
      cellEditor: CellEditorComponent,
      cellEditorParams: {
        fieldType: 'datetime',
        placeholder: 'Select Date & Time (*Required to save)',
        validators: [this.testFormGroup.controls.expectedTime.validator],
      },
      cellRendererSelector: (p: ICellRendererParams) => ({
        component: p.node?.rowPinned
          ? PinnedCellRendererComponent
          : DateTimeCellRenderer,
      }),
      cellRendererParams: {
        fieldType: 'datetime',
        placeholder: 'Select Date & Time (*Required to save)',
      },
      editable: (params) => !params.data.complete,
    },
    // {
    //   headerName: 'Status',
    //   field: 'status',
    //   minWidth: 100,
    //   editable: false,
    //   cellClass: 'cell-status',
    //   cellRendererSelector: (p: ICellRendererParams) => this.getCellRenderer(p),
    //   cellRendererParams: {
    //     disabled: true,
    //   },
    // },
    {
      headerName: ' ',
      field: 'rowOptions',
      pinned: 'right',
      lockPinned: true,
      editable: true,
      width: 60,
      equals: (a, b) => false,
      resizable: false,
      cellEditorSelector: (p: ICellEditorParams) => ({
        component: p.node?.rowPinned ? SaveBtnComponent : RowOptionsComponent,
      }),
      cellEditorParams: {
        showDelete: true,
        showCheckpoint: false, // Submit from row menu disabled; use modal or other flow to submit
        showDuplicate: true,
        tableOptional: true, //show eliipses when only one row
        onDelete: (rowNode: IRowNode) => {
          const checkpointToDelete = rowNode.data;
          const checkpointId = checkpointToDelete?.id;
          const isUnsaved =
            checkpointId &&
            typeof checkpointId === 'string' &&
            checkpointId.startsWith('temp-');

          // For unsaved checkpoints, just remove from grid without saving to backend
          if (isUnsaved) {
            this.gridApi.applyTransaction({ remove: [rowNode.data] });
            return;
          }

          // For saved checkpoints, remove from grid and save to backend
          // Get all current checkpoints from grid (includes unsaved ones)
          const allCheckpoints: CheckpointAttributes[] = [];
          this.gridApi.forEachNode((node) => {
            if (!node.rowPinned && node.data) {
              const checkpoint = this.convertDataToAttr(node.data);
              // Skip the one being deleted
              if (checkpoint.id !== checkpointId) {
                allCheckpoints.push(checkpoint);
              }
            }
          });

          // Remove from grid immediately
          this.gridApi.applyTransaction({ remove: [rowNode.data] });

          // Sanitize checkpoints before saving (remove temp IDs)
          const sanitizedCheckpoints = allCheckpoints
            .map((cp) => {
              if (!cp) return null;
              const copy = JSON.parse(JSON.stringify(cp));
              if (
                copy.id &&
                typeof copy.id === 'string' &&
                copy.id.startsWith('temp-')
              ) {
                copy.id = null;
              }
              return copy;
            })
            .filter((cp) => {
              if (!cp) return false;
              if (
                cp.id &&
                typeof cp.id === 'string' &&
                cp.id.startsWith('temp-')
              ) {
                return false;
              }
              return true;
            });

          this.subscriptions.add(
            this.tripService
              .saveTrip({ id: this.tripId, checkpoints: sanitizedCheckpoints })
              .pipe(
                tap((_) => {
                  this.toastr.success('Trip has been saved', 'Success');
                }),
                catchError((err) => {
                  this.toastr.error('Failed to Save Trip', 'Error');
                  return this.tripId;
                })
              )
              .subscribe()
          );
          return allCheckpoints;
        },
        onDuplicateClick: (rowNode: IRowNode) => {
          this.inputRow = {
            id: null,
            description: rowNode?.data?.description,
            expectedTime: rowNode?.data?.expectedTime,
          };
          // add row to the table
          this.gridApi.applyTransaction({ add: [this.inputRow] });
          this.saveCheckpointData();
          this.inputRow = {};
        },
        onCheckpointClick: (rowNode: IRowNode) => {
          this.checkpointClickEvent?.emit(rowNode);
        },
        onSaveClick: (rowNode: IRowNode) => {
          this.savePinnedRowData();
          return;
        },
        getRowCount: () => {
          return this.rowData?.length;
        },
        getSaveAbility: () => {
          return this.allowSaveOnPinned;
        },
      },
      cellRendererSelector: (p: ICellRendererParams) => ({
        component: p.node?.rowPinned ? SaveBtnComponent : RowOptionsComponent,
      }),
      cellRendererParams: {
        showDelete: true,
        showCheckpoint: false, // Submit from row menu disabled; use modal or other flow to submit
        showDuplicate: true,
        tableOptional: true, //show eliipses when only one row
        canSubmitCheckpoints: () => this.tripIsOpen,
        onDelete: (rowNode: IRowNode) => {
          const checkpointToDelete = rowNode.data;
          const checkpointId = checkpointToDelete?.id;
          const isUnsaved =
            checkpointId &&
            typeof checkpointId === 'string' &&
            checkpointId.startsWith('temp-');

          // For unsaved checkpoints, just remove from grid without saving to backend
          if (isUnsaved) {
            this.gridApi.applyTransaction({ remove: [rowNode.data] });
            return;
          }

          // For saved checkpoints, remove from grid and save to backend
          // Get all current checkpoints from grid (includes unsaved ones)
          const allCheckpoints: CheckpointAttributes[] = [];
          this.gridApi.forEachNode((node) => {
            if (!node.rowPinned && node.data) {
              const checkpoint = this.convertDataToAttr(node.data);
              // Skip the one being deleted
              if (checkpoint.id !== checkpointId) {
                allCheckpoints.push(checkpoint);
              }
            }
          });

          // Remove from grid immediately
          this.gridApi.applyTransaction({ remove: [rowNode.data] });

          // Sanitize checkpoints before saving (remove temp IDs)
          const sanitizedCheckpoints = allCheckpoints
            .map((cp) => {
              if (!cp) return null;
              const copy = JSON.parse(JSON.stringify(cp));
              if (
                copy.id &&
                typeof copy.id === 'string' &&
                copy.id.startsWith('temp-')
              ) {
                copy.id = null;
              }
              return copy;
            })
            .filter((cp) => {
              if (!cp) return false;
              if (
                cp.id &&
                typeof cp.id === 'string' &&
                cp.id.startsWith('temp-')
              ) {
                return false;
              }
              return true;
            });

          this.subscriptions.add(
            this.tripService
              .saveTrip({ id: this.tripId, checkpoints: sanitizedCheckpoints })
              .pipe(
                tap((_) => {
                  this.toastr.success('Trip has been saved', 'Success');
                }),
                catchError((err) => {
                  this.toastr.error('Failed to Save Trip', 'Error');
                  return this.tripId;
                })
              )
              .subscribe()
          );
          return allCheckpoints;
        },
        onDuplicateClick: (rowNode: IRowNode) => {
          this.inputRow = {
            id: null,
            description: rowNode?.data?.description,
            expectedTime: rowNode?.data?.expectedTime,
          };
          // add row to the table
          this.gridApi.applyTransaction({ add: [this.inputRow] });
          this.saveCheckpointData();
          this.inputRow = {};
        },
        onCheckpointClick: (rowNode: IRowNode) => {
          this.checkpointClickEvent?.emit(rowNode);
        },
        onSaveClick: (rowNode: IRowNode) => {
          this.savePinnedRowData();
          return;
        },
        getRowCount: () => {
          return this.rowData?.length;
        },
        getSaveAbility: () => {
          return this.allowSaveOnPinned;
        },
        getRequiredFields: () => {
          return ['description', 'expectedTime'];
        },
      },
      singleClickEdit: false,
    },
  ];

  gridOptions: GridOptions = {
    asyncTransactionWaitMillis: 500,
    columnDefs: this.checkpointColDefs,
    suppressRowHoverHighlight: true,
    noRowsOverlayComponent: NoRowsMessageComponent,
    noRowsOverlayComponentParams: {
      required: false,
    },
    rowClassRules: {
      'late-checkpoint': (params) => params.data.status === 'Late',
      'complete-checkpoint': (params) => params.data.complete,
    },
    getRowId: (params) => params.data.id,
    getRowHeight: (p) => (p.node.rowPinned == 'top' ? 50 : 40),
    onCellValueChanged: (params: CellValueChangedEvent) => {
      let rowNode = params.node;
      let data = rowNode.data;
      let field = params.column.getColId();
      if (params.node?.rowPinned === 'top') {
        return;
      }
      if (field === 'rowOptions') {
        return;
      }
      let valid = this.isTableValid();
      if (valid) {
        this.saveCheckpointData();
      }
    },
    //allow tabbing to row options for accessability
    onCellKeyDown: (event: any) => {
      if (event.event.code === 'Enter' || event.event.code === 'NumpadEnter') {
        const isPinned = event.column.getPinned();
        if (isPinned) {
          // Prevent default and stop propagation to avoid double Enter key requirement
          event.event.preventDefault();
          event.event.stopPropagation();

          let cell = this.gridApi.getCellRendererInstances({
            rowNodes: [event.api.getRowNode(event.node?.id)],
            columns: [event.column],
          })[0];
          if (cell) {
            let renderer = cell as RowOptionsComponent;
            renderer.openMenu();
          }
        }
      }
    },
  };

  redrawCells() {
    this.gridApi.redrawRows();
  }

  // Needed for succesfull tabbing
  onCellFocused(_event: any) {
    // reset top pinned row allows immediate focus on row options for some reason
    this.pinnedTopRowData = [this.inputRow];
  }

  ngOnInit() {}
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.tripDataSubscription.unsubscribe();
  }

  gridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    // make sure dates are visible
    this.gridApi.autoSizeColumns(['dateTime']);
    this.applyReadOnly();
  }

  /** Force a column non-editable and hide its row-delete action (if any). */
  private toReadOnlyColDef(col: ColDef): ColDef {
    const next: ColDef = { ...col, editable: false };
    for (const key of ['cellRendererParams', 'cellEditorParams'] as const) {
      const params = (next as any)[key];
      if (params && typeof params === 'object' && 'showDelete' in params) {
        (next as any)[key] = { ...params, showDelete: false };
      }
    }
    return next;
  }

  /** Apply (or clear) the closed-trip read-only lock on the grid. */
  private applyReadOnly() {
    if (!this.gridApi) return;
    this.gridApi.setGridOption(
      'columnDefs',
      this._readOnly
        ? this.checkpointColDefs.map((c) => this.toReadOnlyColDef(c))
        : this.checkpointColDefs
    );
    this.pinnedTopRowData = this._readOnly ? [] : [this.inputRow];
    this.gridApi.setGridOption('pinnedTopRowData', this.pinnedTopRowData);
  }

  get gridRows() {
    let rows: IRowNode[] = [];
    this.gridApi.forEachNode((node) => {
      if (!node.rowPinned) {
        rows.push(node);
      }
    });
    return rows;
  }

  onCellEditingStopped(params: CellEditingStoppedEvent) {
    if (params.rowPinned !== 'top') {
      return;
    }
    // if pinned row data is filed out then attempt to add it to the list
    if (this.isRowValid(params.node)) {
      // enable/disable the save button
      this.allowSaveOnPinned = true;
      this.gridApi.refreshCells({ force: true });
    } else {
      this.allowSaveOnPinned = false;
      this.gridApi.refreshCells({ force: true });
    }
    return;
  }

  savePinnedRowData() {
    //get pinned row node
    var rowNode = this.gridApi.getPinnedTopRow(0);
    // add row
    this.gridApi.applyTransaction({ add: [rowNode?.data] });
    //reset pinned row
    this.inputRow = {};
    this.pinnedTopRowData = [this.inputRow];
    this.allowSaveOnPinned = false;
    this.saveCheckpointData();
  }

  saveCheckpointData() {
    this.gridApi.forEachNode((node) => {
      // only take in the non-pinned nodes
      if (node.rowPinned === 'top') {
        return;
      } else {
        let temp = this.convertDataToAttr(node.data);
        // add to/update rowData
        if (temp.id) {
          //existing row item so update it
          const index = this.rowData.findIndex((row) => row.id === temp.id);
          this.rowData[index] = temp;
        } else {
          //if not add it
          this.rowData.push(temp);
        }
      }
    });

    // Sanitize checkpoints: remove temporary IDs (starting with 'temp-')
    // The backend will generate proper UUIDs for new checkpoints
    const sanitizedCheckpoints = this.rowData
      .map((cp) => {
        if (!cp) return null;
        // Create a deep copy to avoid mutating the original
        const copy = JSON.parse(JSON.stringify(cp));
        if (
          copy.id &&
          typeof copy.id === 'string' &&
          copy.id.startsWith('temp-')
        ) {
          copy.id = null; // Use null instead of undefined for better JSON serialization
        }
        return copy;
      })
      .filter((cp) => {
        if (!cp) return false;
        // Final safety check: if somehow a temp ID slipped through, remove the entire checkpoint
        if (cp.id && typeof cp.id === 'string' && cp.id.startsWith('temp-')) {
          console.error(
            'CRITICAL: Found temporary ID in checkpoint after sanitization, removing checkpoint:',
            cp.id
          );
          return false;
        }
        return true;
      });

    // Log if we removed any temporary IDs for debugging
    const hadTempIds = this.rowData.some(
      (cp) => cp?.id && typeof cp.id === 'string' && cp.id.startsWith('temp-')
    );
    if (hadTempIds) {
      console.log(
        'Sanitized checkpoints in grid: removed temporary IDs. Original count:',
        this.rowData.length,
        'Sanitized count:',
        sanitizedCheckpoints.length
      );
    }

    this.subscriptions.add(
      this.tripService
        .saveTrip({ id: this.tripId, checkpoints: sanitizedCheckpoints })
        .pipe(
          tap(() => this.toastr.success('Successfully Saved Trip')),
          catchError((err) => {
            this.toastr.error('Failed to Save Trip');
            console.error(err);
            // Return Real Trip on Error
            return this.tripId;
          })
        )
        .subscribe()
    );
  }

  onKeyDown(event: any) {
    if (!this.allowSaveOnPinned) return;
    if (event.event.key === 'Enter' || event.event.code === 'NumpadEnter') {
      const focusedCell = this.gridApi.getFocusedCell();
      if (focusedCell.rowPinned) {
        this.savePinnedRowData();
      } else {
        this.saveCheckpointData();
      }
    }
    return;
  }

  // take ag grid data and transform into saveable data
  convertDataToAttr(rowData): CheckpointAttributes {
    let checkpoint = {
      id: rowData?.id ?? undefined,
      tripId: this.tripId,
      description: rowData?.description ?? null,
      expectedTime: rowData?.expectedTime ?? null,
      status: rowData?.status,
      notes: rowData?.notes ?? null,
      okayId: rowData?.okayId ?? null,
    };
    return checkpoint;
  }

  getCellRenderer(p: ICellRendererParams<any, any, any>) {
    const node = p.node;
    const pinnedRenderer = {
      component: PinnedCellRendererComponent,
    };
    const renderer = {
      component: CellRendererComponent,
    };
    //show pinned renderer only when there is no values
    if (node.rowPinned === 'top') {
      return pinnedRenderer;
    } else {
      return renderer;
    }
  }

  private isPinnedRowDataCompleted(): boolean {
    let descriptionFilledOut = this.inputRow.description?.trim()?.length > 0;
    let dateTimeFilledOut = !Number.isNaN(
      new Date(this.inputRow.expectedTime).valueOf()
    );
    return descriptionFilledOut && dateTimeFilledOut;
  }

  /**
   *
   * @param row
   * @param property The specific property in question, such as 'description'.  May be nested.  Excluding this checks the whole row.
   * @returns
   */
  isRowValid(row: IRowNode<CheckpointAttributes>, property?: string) {
    this.testFormGroup.reset(row.data);
    if (property) {
      // if get(property) isn't there, then this should error because we missed something
      return this.testFormGroup.get(property).valid;
    }
    return this.testFormGroup.valid;
  }
  isTableValid() {
    if (!this.gridApi) {
      this.errors.emit(null);
      return true;
    }
    let errors = [];
    let everythingValid = true;
    this.gridApi.forEachNode((node) => {
      const checkpointSubmitted = ['Missed', 'Submitted'].includes(
        node.data.status
      );
      this.testFormGroup.reset(node.data);
      errors.push(this.testFormGroup.errors);
      if (!checkpointSubmitted && !this.isRowValid(node)) {
        everythingValid = false;
      }
    });

    this.errors.emit(everythingValid ? null : errors);
    return everythingValid;
  }
}
